import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { ModelViewerItem } from '../useModelViewerEditor/useModelViewerEditor.types';

const isValidModelFile = (file: File) => {
  const validExtensions = ['.glb', '.gltf'];
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf('.'));

  return validExtensions.includes(fileExtension);
};

function useModelViewerDropZone() {
  const [isDragging, setIsDragging] = useState(false);
  const [currentModel, setCurrentModel] = useState<ModelViewerItem | null>(
    null
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const dropZoneRootEl = useRef<HTMLInputElement>(null);
  const [dropError, setDropError] = useState('');
  const dropZoneCounter = useRef(0);

  useEffect(() => {
    const onDocumentDragLeave = (e: globalThis.DragEvent) => {
      if (e.relatedTarget === null) {
        dropZoneCounter.current = 0;
        setIsDragging(false);
      }
    };

    const onDocumentDrop = (e: globalThis.DragEvent) => {
      if (
        dropZoneRootEl.current &&
        e.target instanceof Node &&
        !dropZoneRootEl.current.contains(e.target)
      ) {
        dropZoneCounter.current = 0;
        setIsDragging(false);
      }
    };

    document.addEventListener('dragleave', onDocumentDragLeave);
    document.addEventListener('drop', onDocumentDrop);

    return () => {
      document.removeEventListener('dragleave', onDocumentDragLeave);
      document.removeEventListener('drop', onDocumentDrop);
    };
  }, []);

  const handleDropClick = () => {
    fileInput.current?.click();
  };

  const handleDropInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setDropError('Only model files are allowed');
      return;
    }

    if (!isValidModelFile(file)) {
      setDropError('Supports only this formats: glb, gltf');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setCurrentModel((prev) => {
      if (prev?.file.startsWith('blob:')) URL.revokeObjectURL(prev.file);
      return { file: objectUrl, name: file.name };
    });
  };

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    dropZoneCounter.current++;

    setIsDragging(true);
    setDropError('');
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    dropZoneCounter.current--;

    if (dropZoneCounter.current > 0) return;

    dropZoneCounter.current = 0;
    setIsDragging(false);
  }, []);

  const setCurrentModelExternal = useCallback(
    (item: ModelViewerItem) => {
      if (currentModel && currentModel.file.startsWith('blob:')) {
        URL.revokeObjectURL(currentModel.file);
      }
      setCurrentModel(item);
    },
    [currentModel]
  );

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dropZoneCounter.current = 0;
    setIsDragging(false);

    setDropError('');

    const file = e.dataTransfer.files?.[0];
    if (!file) {
      setDropError('Only model files are allowed');
      return;
    }

    if (!isValidModelFile(file)) {
      setDropError('Supports only this formats: glb, gltf');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setCurrentModel((prev) => {
      if (prev?.file.startsWith('blob:')) URL.revokeObjectURL(prev.file);
      return { file: objectUrl, name: file.name };
    });
  };

  return {
    isDragging,
    currentModel,
    fileInput,
    dropZoneRootEl,
    dropError,
    handleDropClick,
    handleDropInputChange,
    handleDrop,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    setCurrentModelExternal
  };
}

export default useModelViewerDropZone;
